import { hasCompanyRole, hasManagerRole } from '../utils/role.utils';

/** WP session / users/me shape used for role-power checks. */
export type UserRoleContext = { role?: string | string[] };

export type CreatableUserRoleOption = {
  value: 'employee' | 'manager' | 'lead';
  label: string;
  description: string;
};

const ROLE_COPY: Record<'employee' | 'manager' | 'lead', { label: string; description: string }> = {
  employee: {
    label: 'Employee',
    description: 'Monitored team member (uses a seat).',
  },
  manager: {
    label: 'Manager',
    description: 'Can manage employees and teams. No billing access.',
  },
  lead: {
    label: 'Team Lead',
    description: 'View reports and monitoring for assigned teams only.',
  },
};

/**
 * Roles the current user may assign when creating a user.
 * Company on Pro+: employee, manager, lead. Manager on Pro+: employee, lead (not manager).
 */
export function getCreatableUserRoleOptions(
  loggedInUser: UserRoleContext | null | undefined,
  hasManagerRolesEntitlement: boolean
): CreatableUserRoleOption[] {
  const options: CreatableUserRoleOption[] = [
    { value: 'employee', ...ROLE_COPY.employee },
  ];

  if (!hasManagerRolesEntitlement) {
    return options;
  }

  if (hasCompanyRole(loggedInUser)) {
    options.push(
      { value: 'manager', ...ROLE_COPY.manager },
      { value: 'lead', ...ROLE_COPY.lead }
    );
    return options;
  }

  if (hasManagerRole(loggedInUser)) {
    options.push({ value: 'lead', ...ROLE_COPY.lead });
  }

  return options;
}

/** Company or manager may add/edit employees; team lead may not. */
export function canManageCompanyEmployees(
  loggedInUser: UserRoleContext | null | undefined
): boolean {
  return hasCompanyRole(loggedInUser) || hasManagerRole(loggedInUser);
}

/** Only company may create Manager login users (Pro+). */
export function canAssignManagerUserRole(
  loggedInUser: UserRoleContext | null | undefined,
  hasManagerRolesEntitlement: boolean
): boolean {
  return hasManagerRolesEntitlement && hasCompanyRole(loggedInUser);
}

/** Company or manager may create Team Lead login users (Pro+). */
export function canAssignLeadUserRole(
  loggedInUser: UserRoleContext | null | undefined,
  hasManagerRolesEntitlement: boolean
): boolean {
  return (
    hasManagerRolesEntitlement &&
    (hasCompanyRole(loggedInUser) || hasManagerRole(loggedInUser))
  );
}

export function getRoleSelectHint(
  loggedInUser: UserRoleContext | null | undefined,
  hasManagerRolesEntitlement: boolean
): string {
  if (hasManagerRole(loggedInUser) && !hasCompanyRole(loggedInUser)) {
    return 'Managers can add employees and team leads within your purchased seat quota. Only the company account can add another Manager.';
  }
  if (!hasManagerRolesEntitlement) {
    return 'Upgrade to Professional to add Manager and Team Lead users.';
  }
  if (hasCompanyRole(loggedInUser)) {
    return 'Manager: full ops admin without billing. Team Lead: monitoring and reports only.';
  }
  return '';
}

export function isElevatedAssignableRole(role: string): boolean {
  const r = String(role || '').toLowerCase();
  return r === 'manager' || r === 'lead';
}

export function validateRoleAssignmentForEditor(
  loggedInUser: UserRoleContext | null | undefined,
  requestedRole: string,
  hasManagerRolesEntitlement: boolean
): string | null {
  const role = String(requestedRole || 'employee').toLowerCase();
  if (!isElevatedAssignableRole(role)) {
    return null;
  }
  if (!hasManagerRolesEntitlement) {
    return 'Manager and Team Lead roles require a Professional plan or higher.';
  }
  if (role === 'manager' && !canAssignManagerUserRole(loggedInUser, hasManagerRolesEntitlement)) {
    return 'Only the company account can create Manager users.';
  }
  if (role === 'lead' && !canAssignLeadUserRole(loggedInUser, hasManagerRolesEntitlement)) {
    return 'Team Lead users require a Professional plan and a company or manager account.';
  }
  return null;
}
